import React, { useState } from 'react';
import type { Temps, AppData } from '../types';
import { Button, Card, Input, Select } from './UI';

interface TempsBlocProps {
  data: AppData;
  onDataChange: (data: AppData) => void;
}

export function TempsList({ data, onDataChange }: TempsBlocProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<Temps>>({});

  const dossiers = data.dossiers;
  const temps = data.temps.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const handleCreate = () => {
    setIsCreating(true);
    setFormData({
      id: `tm${Date.now()}`,
      dossier: dossiers[0]?.id || '',
      desc: '',
      type: 'horaire',
      mins: 0,
      montant: null,
      date: new Date().toISOString().split('T')[0],
      facture: false,
    });
  };

  const handleEdit = (temps: Temps) => {
    setEditingId(temps.id);
    setFormData(temps);
  };

  const handleSave = () => {
    if (!formData.dossier || !formData.desc || !formData.date) {
      alert('Veuillez remplir les champs requis');
      return;
    }

    const newData = { ...data };
    const dossier = newData.dossiers.find((d) => d.id === formData.dossier);
    const hours = (formData.mins || 0) / 60;
    const tauxHoraire = dossier?.tauxHoraire || 200;

    if (formData.type === 'horaire' && !formData.montant) {
      formData.montant = Math.round(hours * tauxHoraire * 100) / 100;
    }

    if (isCreating) {
      newData.temps.push(formData as Temps);
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.dossier!,
        action: 'Temps enregistré',
        detail: `${formData.desc} (${Math.floor((formData.mins || 0) / 60)}h${((formData.mins || 0) % 60).toString().padStart(2, '0')})`,
        ts: new Date().toISOString(),
      });
    } else {
      newData.temps = newData.temps.map((t) =>
        t.id === editingId ? (formData as Temps) : t
      );
      newData.journal.push({
        id: `j${Date.now()}`,
        dossier: formData.dossier!,
        action: 'Temps modifié',
        detail: formData.desc!,
        ts: new Date().toISOString(),
      });
    }

    onDataChange(newData);
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  const handleDelete = (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce temps enregistré ?')) return;

    const temps = data.temps.find((t) => t.id === id);
    const newData = { ...data };
    newData.temps = newData.temps.filter((t) => t.id !== id);
    newData.journal.push({
      id: `j${Date.now()}`,
      dossier: temps?.dossier || '',
      action: 'Temps supprimé',
      detail: temps?.desc || '',
      ts: new Date().toISOString(),
    });

    onDataChange(newData);
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingId(null);
    setFormData({});
  };

  const toggleFacturation = (id: string) => {
    const newData = { ...data };
    const index = newData.temps.findIndex((t) => t.id === id);
    if (index >= 0) {
      newData.temps[index].facture = !newData.temps[index].facture;
      onDataChange(newData);
    }
  };

  if (isCreating || editingId) {
    const dossier = dossiers.find((d) => d.id === formData.dossier);
    const tauxHoraire = dossier?.tauxHoraire || 200;
    const hours = (formData.mins || 0) / 60;
    const estimatedAmount = Math.round(hours * tauxHoraire * 100) / 100;

    return (
      <div>
        <h1 style={{ margin: '0 0 1.5rem', fontSize: 28 }}>
          {isCreating ? 'Enregistrer du temps' : 'Éditer le temps'}
        </h1>

        <Card style={{ maxWidth: 600 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                Dossier *
              </label>
              <Select
                value={formData.dossier || ''}
                onChange={(e) => setFormData({ ...formData, dossier: e.target.value })}
              >
                <option value="">Sélectionner un dossier</option>
                {dossiers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.ref} — {d.client}
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                Description *
              </label>
              <Input
                value={formData.desc || ''}
                onChange={(e) => setFormData({ ...formData, desc: e.target.value })}
                placeholder="Ex: Rédaction conclusions"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Type *
                </label>
                <Select
                  value={formData.type || 'horaire'}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                >
                  <option value="horaire">Horaire</option>
                  <option value="forfait">Forfait</option>
                </Select>
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Date *
                </label>
                <Input
                  type="date"
                  value={formData.date || ''}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                />
              </div>
            </div>

            {formData.type === 'horaire' && (
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Temps (minutes) *
                </label>
                <Input
                  type="number"
                  value={formData.mins || ''}
                  onChange={(e) => setFormData({ ...formData, mins: parseInt(e.target.value) || 0 })}
                  placeholder="Ex: 120 pour 2 heures"
                />
                <p style={{ margin: '0.5rem 0 0', fontSize: 11, color: 'var(--color-text-secondary)' }}>
                  {hours.toFixed(1)}h → {estimatedAmount}€ (taux {tauxHoraire}€/h)
                </p>
              </div>
            )}

            {formData.type === 'forfait' && (
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4, fontWeight: 500 }}>
                  Montant (€) *
                </label>
                <Input
                  type="number"
                  value={formData.montant || ''}
                  onChange={(e) => setFormData({ ...formData, montant: parseFloat(e.target.value) || 0 })}
                  placeholder="Ex: 250"
                />
              </div>
            )}

            {dossier && (
              <div style={{ padding: '0.75rem', backgroundColor: 'var(--color-background-secondary)', borderRadius: 'var(--border-radius-md)', fontSize: 12 }}>
                <strong>{dossier.ref}</strong> — {dossier.client}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <Button onClick={handleCancel} variant="secondary">
                Annuler
              </Button>
              <Button onClick={handleSave}>
                {isCreating ? 'Enregistrer' : 'Modifier'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const totalHoraireTime = temps.filter((t) => t.type === 'horaire').reduce((sum, t) => sum + (t.mins || 0), 0);
  const totalHours = totalHoraireTime / 60;
  const totalAmount = temps.reduce((sum, t) => sum + (t.montant || 0), 0);
  const invoicedAmount = temps.filter((t) => t.facture).reduce((sum, t) => sum + (t.montant || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Temps</h1>
        <Button onClick={handleCreate}>+ Enregistrer du temps</Button>
      </div>

      {/* Stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem',
        }}
      >
        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)' }}>
            Total (horaire)
          </p>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
            {totalHours.toFixed(1)}h
          </p>
        </Card>
        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)' }}>
            Total (montant)
          </p>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
            {totalAmount}€
          </p>
        </Card>
        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)' }}>
            Facturé
          </p>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
            {invoicedAmount}€
          </p>
        </Card>
        <Card>
          <p style={{ margin: '0 0 0.5rem', fontSize: 11, color: 'var(--color-text-secondary)' }}>
            À facturer
          </p>
          <p style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>
            {(totalAmount - invoicedAmount).toFixed(2)}€
          </p>
        </Card>
      </div>

      {temps.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {temps.map((t) => {
            const dossier = dossiers.find((d) => d.id === t.dossier);
            const hours = (t.mins || 0) / 60;
            return (
              <Card key={t.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 0.25rem', fontSize: 13, fontWeight: 500 }}>
                      {t.desc}
                    </p>
                    <p style={{ margin: '0 0 0.5rem', fontSize: 12, color: 'var(--color-text-secondary)' }}>
                      {dossier?.ref} — {dossier?.client}
                    </p>
                    <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                      {t.date} • {t.type === 'horaire' ? `${hours.toFixed(1)}h` : 'Forfait'} • {t.montant}€
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'start' }}>
                    <button
                      onClick={() => toggleFacturation(t.id)}
                      style={{
                        padding: '4px 10px',
                        backgroundColor: t.facture ? 'var(--color-accent)' : 'var(--color-background-secondary)',
                        color: t.facture ? '#ffffff' : 'var(--color-text-primary)',
                        border: 'none',
                        borderRadius: 'var(--border-radius-sm)',
                        fontSize: 11,
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      {t.facture ? '✓ Facturé' : 'À facturer'}
                    </button>
                    <Button onClick={() => handleEdit(t)} size="sm" variant="secondary">
                      ✎
                    </Button>
                    <Button onClick={() => handleDelete(t.id)} size="sm" variant="secondary">
                      🗑
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1rem' }}>
            Aucun temps enregistré
          </p>
          <Button onClick={handleCreate}>Enregistrer le premier temps</Button>
        </Card>
      )}
    </div>
  );
}
